<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import OffersAPI from 'dashboard/api/offers';
import VoipDateTimePicker from './VoipDateTimePicker.vue';

// Form puro de finalização — sem chrome de modal. Usado em:
//   - CallDispositionModal.vue (envolto em overlay, Commit 1 aceita pending)
//   - VoipCallReview.vue (embed inline na detail pane — sempre tem callRecord)
//
// Props:
//   call-record: { id, disposition, notes, ... } — nullable a partir de Commit 1
//   pending-call-end: { provider_call_id, peer_phone, ... } — usado quando
//     callRecord é null (chamada terminou, webhook ainda nao chegou).
//   show-transcription-panel: boolean (modal usa true; review false porque já tem painel próprio)
// Emits:
//   saved: payload — após save bem-sucedido
//   dismissed: () — usuário clicou "preencher depois" (modal only)

const props = defineProps({
  callRecord: { type: Object, default: null },
  pendingCallEnd: { type: Object, default: null },
  showTranscriptionPanel: { type: Boolean, default: false },
  showDismiss: { type: Boolean, default: false },
});
const emit = defineEmits(['saved', 'dismissed']);

const store = useStore();
const config  = computed(() => store.getters['voipDisposition/getConfig']);
const uiFlags = computed(() => store.getters['voipDisposition/getUIFlags']);

const form = ref({
  disposition: '',
  notes: '',
  next_contact_at: '',
  appointment_date: '',
  lead_temperature: '',
  disposition_custom: {},
  // Phase 2.9.10 c2: Valor do negócio (modo livre digita centavos,
  // modo offer grava offer.id + offer.value_cents).
  business_value_cents: null,
  linked_offer_id: null,
});
const errors = ref({});

// Phase 2.9.10 c2: catalogo de offers (carregado on-demand quando o campo
// business_value tiver mode=offer). Falha silenciosa — UI cai em lista
// vazia e agente pode só digitar livre.
const offers = ref([]);

watch(() => props.callRecord, newRecord => {
  if (!newRecord) {
    // Sem CallRecord ainda — form comeca vazio. Nao reseta se o usuario
    // ja digitou algo (watch so reage quando callRecord fica !== anterior).
    return;
  }
  form.value = {
    disposition:        newRecord.disposition || '',
    notes:              newRecord.notes || '',
    next_contact_at:    newRecord.next_contact_at ? newRecord.next_contact_at.slice(0, 16) : '',
    appointment_date:   newRecord.appointment_date ? newRecord.appointment_date.slice(0, 16) : '',
    lead_temperature:   newRecord.lead_temperature || '',
    disposition_custom: newRecord.disposition_custom ? { ...newRecord.disposition_custom } : {},
    // Phase 2.9.10 c2
    business_value_cents: newRecord.business_value_cents ?? null,
    linked_offer_id:      newRecord.linked_offer_id ?? null,
  };
  errors.value = {};
}, { immediate: true });

// Phase 2.9.10 c2: carrega offers quando business_value esta habilitado
// em modo catalogo. Filtra por offer_group_id se configurado.
// Usa OffersAPI (wrapper autenticado) em vez de axios cru — o axios
// importado dinamicamente nao carrega os headers de auth injetados
// pelo APIHelper.js, resultando em 401 silencioso.
watch(() => config.value?.business_value_config, async (cfg) => {
  if (!cfg?.enabled || cfg.mode !== 'offer') return;
  try {
    const { data } = await OffersAPI.getOffers();
    const all = Array.isArray(data) ? data : (data?.offers || data?.data || []);
    if (cfg.linked_offer_group_id) {
      offers.value = all.filter(o => o.offer_group_id === cfg.linked_offer_group_id);
    } else {
      offers.value = all;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[CallDispositionForm] Falha ao carregar ofertas:', e);
    offers.value = [];
  }
}, { immediate: true });

const isEnabled  = field => !!config.value?.enabled_fields?.[field];
const isRequired = field => !!config.value?.required_fields?.[field];
// Spec 2026-04-23: options agora e array de hashes { name, funnel_id,
// funnel_stage }. UI do modal so precisa dos nomes no dropdown; backend
// recebe `disposition: name` como sempre.
const options    = computed(() => {
  const raw = config.value?.options || [];
  return raw.map(o => (typeof o === 'string' ? { name: o } : (o || {}))).filter(o => o.name);
});
const optionNames = computed(() => options.value.map(o => o.name));
const customFields = computed(() => config.value?.custom_fields || []);
const temperatureLevels = computed(() => config.value?.constants?.lead_temperatures || []);

// Phase 2.9.10 c2
const businessValueConfig = computed(() => config.value?.business_value_config || { enabled: false, mode: 'free' });
const businessValueEnabled = computed(() => !!businessValueConfig.value.enabled);
const businessValueRequired = computed(() => !!businessValueConfig.value.required);
const businessValueMode = computed(() => businessValueConfig.value.mode || 'free');

// Helper que converte "1.234,56" ou "1234.56" em centavos (integer).
function brlToCents(str) {
  if (str === null || str === '') return null;
  const normalized = String(str).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? Math.round(num * 100) : null;
}
function centsToBrl(cents) {
  if (cents == null) return '';
  return (cents / 100).toFixed(2).replace('.', ',');
}

// Wrapper reativo pro input de valor livre — usuario digita em BRL,
// armazenamos em centavos.
const businessValueInput = computed({
  get: () => centsToBrl(form.value.business_value_cents),
  set: (v) => { form.value.business_value_cents = brlToCents(v); }
});

// Quando seleciona oferta do catalogo, copia o valor dela pro cents.
function onOfferSelected(offerId) {
  const id = offerId ? parseInt(offerId, 10) : null;
  form.value.linked_offer_id = id;
  if (id) {
    const offer = offers.value.find(o => o.id === id);
    if (offer) {
      // offer.value vem do backend em unidade inteira (reais), converte
      form.value.business_value_cents = Math.round(parseFloat(offer.value || 0) * 100);
    }
  } else {
    form.value.business_value_cents = null;
  }
}
const showStaleDispositionWarning = computed(() => {
  const current = form.value.disposition;
  return current && !optionNames.value.includes(current);
});

const isAlreadyFilled = computed(() => !!props.callRecord?.disposition_filled_at);
const lastSavedLabel = computed(() => {
  if (!props.callRecord?.disposition_filled_at) return null;
  const dt = new Date(props.callRecord.disposition_filled_at);
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
});

// Phase 2.5 A: atalhos de preenchimento rapido.
// Presets vem da config; cada um define quais campos preenche.
// Aplicacao: clique no botao OU pressionar tecla 1-9 (so quando form ta focado).
const presets = computed(() => config.value?.presets || []);

// Resolve offset strings como "+1d@09:00" / "today@18:00" pra ISO
// que VoipDateTimePicker aceita (YYYY-MM-DDTHH:MM).
function resolveOffset(offsetStr) {
  if (!offsetStr) return '';
  const match = offsetStr.match(/^(today|\+(\d+)d)(?:@(\d{2}):(\d{2}))?$/);
  if (!match) return '';
  const d = new Date();
  if (match[2]) d.setDate(d.getDate() + parseInt(match[2], 10));
  // today mantem hoje
  const h = parseInt(match[3] || '09', 10);
  const m = parseInt(match[4] || '00', 10);
  d.setHours(h, m, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(h).padStart(2, '0');
  const mi = String(m).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function applyPreset(preset) {
  if (!preset?.fields) return;
  const f = preset.fields;
  if (f.disposition)      form.value.disposition      = f.disposition;
  if (f.notes)            form.value.notes            = f.notes;
  if (f.lead_temperature) form.value.lead_temperature = f.lead_temperature;
  if (f.next_contact_at_offset)   form.value.next_contact_at  = resolveOffset(f.next_contact_at_offset);
  if (f.appointment_date_offset)  form.value.appointment_date = resolveOffset(f.appointment_date_offset);
  if (f.custom && typeof f.custom === 'object') {
    form.value.disposition_custom = { ...form.value.disposition_custom, ...f.custom };
  }
}

// Listener de tecla: ativa quando tecla 1-9 pressionada e o form esta visivel.
// Ignora se target eh input/textarea/select pra nao atrapalhar digitacao normal.
function onKeydown(e) {
  if (!/^[1-9]$/.test(e.key)) return;
  const tag = (e.target?.tagName || '').toLowerCase();
  if (['input', 'textarea', 'select'].includes(tag)) return;
  const preset = presets.value.find(p => p.hotkey === e.key);
  if (preset) { applyPreset(preset); e.preventDefault(); }
}
onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

async function handleSubmit() {
  errors.value = {};
  try {
    const payload = {
      disposition:        form.value.disposition || null,
      notes:              form.value.notes || null,
      next_contact_at:    form.value.next_contact_at || null,
      appointment_date:   form.value.appointment_date || null,
      lead_temperature:   form.value.lead_temperature || null,
      disposition_custom: form.value.disposition_custom,
      // Phase 2.9.10 c2
      business_value_cents: form.value.business_value_cents,
      linked_offer_id:      form.value.linked_offer_id,
    };

    let result;
    // Phase 2.9.10 Commit 1: quando usado no modal pos-chamada, SEMPRE passa
    // pelo endpoint novo que lida com CallRecord ausente. Quando embed inline
    // no Review (sempre tem callRecord filled), pode usar o PATCH legacy.
    if (props.pendingCallEnd || !props.callRecord?.id) {
      result = await store.dispatch('voipDisposition/saveDispositionFromModal', {
        pendingCallEnd: props.pendingCallEnd,
        callRecord:     props.callRecord,
        payload,
      });
    } else {
      result = await store.dispatch('voipDisposition/saveDisposition', {
        callRecordId: props.callRecord.id,
        payload,
      });
    }
    emit('saved', result);
  } catch (e) {
    errors.value = e?.response?.data?.errors || { base: 'Não foi possível salvar.' };
  }
}
</script>

<template>
  <div class="space-y-4">
    <p v-if="isAlreadyFilled" class="text-xs text-n-slate-11 italic">
      ✓ Última edição: {{ lastSavedLabel }}
    </p>

    <!-- Phase 2.5 A: Atalhos (presets) -->
    <div v-if="presets.length" class="rounded-xl border border-n-brand/30 bg-n-brand/5 p-2">
      <p class="text-[10px] font-medium text-n-slate-11 uppercase mb-1.5 px-1">Atalhos · aperte a tecla ou clique</p>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="preset in presets" :key="preset.hotkey || preset.label" type="button"
                class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-n-solid-1 border border-n-weak hover:border-n-brand hover:bg-n-brand/10 text-sm text-n-slate-12 transition-colors"
                :title="`Preenche: ${preset.fields?.disposition || '—'} · ${preset.fields?.lead_temperature || ''}`"
                @click="applyPreset(preset)">
          <kbd v-if="preset.hotkey" class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-mono font-bold rounded bg-n-alpha-2 border border-n-weak">{{ preset.hotkey }}</kbd>
          <span class="text-base leading-none">{{ preset.icon || '⚡' }}</span>
          <span class="font-medium">{{ preset.label }}</span>
        </button>
      </div>
    </div>

    <!-- Disposition (sempre obrigatório config-default) -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        Resultado
        <span v-if="isRequired('disposition')" class="text-n-ruby-9">*</span>
      </label>
      <select v-model="form.disposition"
              class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12">
        <option value="">— Selecione —</option>
        <option v-for="opt in options" :key="opt.name" :value="opt.name">{{ opt.name }}</option>
        <option v-if="showStaleDispositionWarning" :value="form.disposition" disabled>
          {{ form.disposition }} (opção removida)
        </option>
      </select>
      <p v-if="errors.disposition" class="text-xs text-n-ruby-9 mt-1">{{ errors.disposition }}</p>
    </div>

    <div v-if="isEnabled('notes')">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        Anotações
        <span v-if="isRequired('notes')" class="text-n-ruby-9">*</span>
      </label>
      <textarea v-model="form.notes" rows="3"
                class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12 resize-none"
                placeholder="Observações sobre a ligação (opcional)" />
      <p v-if="errors.notes" class="text-xs text-n-ruby-9 mt-1">{{ errors.notes }}</p>
    </div>

    <div v-if="isEnabled('next_contact_at')">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        Próximo contato
        <span v-if="isRequired('next_contact_at')" class="text-n-ruby-9">*</span>
      </label>
      <VoipDateTimePicker v-model="form.next_contact_at" placeholder="Quando ligar de novo" />
    </div>

    <div v-if="isEnabled('appointment_date')">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        Data do agendamento
        <span v-if="isRequired('appointment_date')" class="text-n-ruby-9">*</span>
      </label>
      <VoipDateTimePicker v-model="form.appointment_date" placeholder="Data/hora da consulta ou reunião" />
    </div>

    <!-- Phase 2.9 (P1): temperatura do lead e preenchida so pela IA (via lead scoring).
         Agente nao escolhe mais manualmente. Mantemos a coluna no DB, so escondemos
         o input visual — lead_temperature continua setado pelo Voip::LeadScoringService. -->
    <div v-if="form.lead_temperature" class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11">
      🤖 Temperatura atual: <strong class="text-n-slate-12">{{ form.lead_temperature }}</strong>
      <span class="opacity-70">(preenchido automaticamente pela IA)</span>
    </div>

    <!-- Phase 2.9.10 c2: Valor do negócio -->
    <div v-if="businessValueEnabled">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        💰 Valor do negócio
        <span v-if="businessValueRequired" class="text-n-ruby-9">*</span>
      </label>

      <!-- Modo livre: input numerico formatado em BRL -->
      <div v-if="businessValueMode === 'free'" class="relative">
        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-n-slate-11 pointer-events-none">R$</span>
        <input v-model="businessValueInput" type="text" inputmode="decimal"
               placeholder="0,00"
               class="w-full rounded-xl border border-n-weak bg-n-alpha-1 pl-10 pr-3 py-2 text-n-slate-12" />
      </div>

      <!-- Modo catálogo: dropdown das Offers -->
      <div v-else-if="businessValueMode === 'offer'">
        <select :value="form.linked_offer_id ?? ''"
                class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
                @change="onOfferSelected($event.target.value)">
          <option value="">— Selecione uma oferta —</option>
          <option v-for="o in offers" :key="o.id" :value="o.id">
            {{ o.title }} · R$ {{ Number(o.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}{{ o.currency && o.currency !== 'BRL' ? ` (${o.currency})` : '' }}
          </option>
        </select>
        <p v-if="!offers.length" class="text-[11px] text-n-amber-11 mt-1">
          Nenhuma oferta cadastrada. Configure em Kanban → Ofertas.
        </p>
      </div>
    </div>

    <div v-for="field in customFields" :key="field.key">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ field.label }}
        <span v-if="field.required" class="text-n-ruby-9">*</span>
      </label>
      <input v-if="['text', 'number', 'date', 'datetime'].includes(field.type)"
             v-model="form.disposition_custom[field.key]"
             :type="field.type === 'datetime' ? 'datetime-local' : field.type"
             class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12" />
      <select v-else-if="field.type === 'enum'"
              v-model="form.disposition_custom[field.key]"
              class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12">
        <option value="">— Selecione —</option>
        <option v-for="opt in (field.options || [])" :key="opt" :value="opt">{{ opt }}</option>
      </select>
      <label v-else-if="field.type === 'bool'" class="inline-flex items-center gap-2">
        <input v-model="form.disposition_custom[field.key]" type="checkbox" class="rounded border-n-weak" />
        <span class="text-sm text-n-slate-11">{{ field.label }}</span>
      </label>
      <p v-if="errors[`disposition_custom.${field.key}`]" class="text-xs text-n-ruby-9 mt-1">
        {{ errors[`disposition_custom.${field.key}`] }}
      </p>
    </div>

    <p v-if="errors.base" class="text-sm text-n-ruby-9">{{ errors.base }}</p>

    <div class="flex items-center justify-between pt-2">
      <button v-if="showDismiss" type="button"
              class="text-sm text-n-slate-11 hover:text-n-slate-12"
              :disabled="uiFlags.isSavingDisposition"
              @click="emit('dismissed')">
        Preencher depois
      </button>
      <span v-else></span>
      <button type="button"
              class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50"
              :disabled="uiFlags.isSavingDisposition"
              @click="handleSubmit">
        {{ uiFlags.isSavingDisposition ? 'Salvando...' : (isAlreadyFilled ? 'Atualizar' : 'Salvar') }}
      </button>
    </div>
  </div>
</template>
